import { Close } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { buildingType } from '../../models/building-type.enum';
import { defaultUnitWishList as defaultUnitWishListCorridor } from '../../Corridor/models/props-config';
import { defaultUnitWishList as defaultUnitWishListRowhouse } from '../../Rowhouse/models/props-config';
import { defaultUnitWishList as defaultUnitWishListTower } from '../../Tower/models/props-config';
import { defaultUnitWishList as defaultUnitWishListHybrid, defaultUnitWishListGallery } from '../../Hybrid/models/props-config';
import { activeStateEnum, initialPropsStepEnum, InitProperties } from '../BuildingInitialPropertiesDialog/init-props';
import styles from './LivingMixSelection.module.css';
import LivingMixUnitCard, { unitSubtypeEnum, UnitWishExtension } from './LivingMixUnitCard/LivingMixUnitCard';
import { unitBaseTypeEnum } from '../../../../unit-base-type-enum';
import { UnitJ } from '../../../../models/section-json';
import { additionalDataForBaseUnitType } from './living-mix-data';
import { UnitWish } from '../../../../models/unit-wish-list';
import { explanationIds } from '../../../../common/authentication/models/user';
import { ServiceManager } from '../../../../services/service-manager';


interface LivingMixSelectionProps {
    open: boolean,
    buildingInitProps: InitProperties,
    unitsJSON: UnitJ[],
    setOpen: (open: boolean) => void,
    setBuildingInitProps: (buildingInitProps: InitProperties) => void
}

const LivingMixSelection: FC<LivingMixSelectionProps> = ({ open, buildingInitProps, unitsJSON, setOpen, setBuildingInitProps }) => {

    const [apartmentType, setApartmentType] = useState<unitSubtypeEnum>(unitSubtypeEnum.apartment);
    const [totalPercentageApartments, setTotalPercentageApartments] = useState<number>(100);
    const [totalPercentageMaisonettes, setTotalPercentageMaisonettes] = useState<number>(100);

    useEffect(() => {
        if (buildingInitProps) {
            const apartmentLivingMix = buildingInitProps.unitWishList.filter(s => s.subtype === unitSubtypeEnum.apartment);
            const maisonetteLivingMix = buildingInitProps.unitWishList.filter(s => s.subtype === unitSubtypeEnum.maisonette);

            let aTotal = apartmentLivingMix.map(s => s.percentage).reduce((p, c) => p + c, 0);
            let mTotal = maisonetteLivingMix.map(s => s.percentage).reduce((p, c) => p + c, 0);

            if (Math.abs(100 - aTotal) < 0.1) {
                aTotal = 100;
            }
            if (Math.abs(100 - mTotal) < 0.1) {
                mTotal = 100;
            }

            setTotalPercentageApartments(aTotal);
            setTotalPercentageMaisonettes(mTotal);
        }
    }, [buildingInitProps])

    const handleClose = () => {
        setOpen(false);
    };

    const handlePrevious = () => {
        setBuildingInitProps({ ...buildingInitProps, stepState: initialPropsStepEnum.buildingType });
    };

    const handleFinished = () => {
        setBuildingInitProps({ ...buildingInitProps, stepState: initialPropsStepEnum.finished });
        setTimeout(()=>{
            ServiceManager.explanationService.showExplanation(explanationIds.buildingNavigation);
        }, 500)
    };

    const setLivingMix = (mix: UnitWishExtension[]) => {
        setBuildingInitProps({ ...buildingInitProps, unitWishList: mix.map(m => ({ ...m })) });
    }

    useEffect(() => {
        const livingMix = getWoonmixModelBasedOnType(buildingInitProps.type);
        setLivingMix(livingMix);
    }, [buildingInitProps.type])

    const computeCleanPercentages = (newMix: UnitWishExtension[], subtypeToUpdate: unitSubtypeEnum) => {

        if (subtypeToUpdate === unitSubtypeEnum.apartment) {
            const apartmentLivingMixClean = newMix.filter(s => s.subtype === unitSubtypeEnum.apartment && !s.dirty && s.active);
            const apartmentLivingMixDirty = newMix.filter(s => s.subtype === unitSubtypeEnum.apartment && s.dirty && s.active);

            const dirtyPercentage = apartmentLivingMixDirty.map(s => s.percentage).reduce((p, c) => p + c, 0);

            const spreadPercentage = +((100 - dirtyPercentage) / apartmentLivingMixClean.length).toFixed(2);

            apartmentLivingMixClean.forEach(a => a.percentage = spreadPercentage);
        }

        if (subtypeToUpdate === unitSubtypeEnum.maisonette) {
            const maisonetteLivingMixClean = newMix.filter(s => s.subtype === unitSubtypeEnum.maisonette && !s.dirty && s.active);
            const maisonetteLivingMixDirty = newMix.filter(s => s.subtype === unitSubtypeEnum.maisonette && s.dirty && s.active);

            const dirtyPercentage = maisonetteLivingMixDirty.map(s => s.percentage).reduce((p, c) => p + c, 0);
            const spreadPercentage = +((100 - dirtyPercentage) / maisonetteLivingMixClean.length).toFixed(2);
            maisonetteLivingMixClean.forEach(m => m.percentage = spreadPercentage);
        }

    }

    const toggleActiveLivingMixType = (toggleType: unitBaseTypeEnum) => {
        let subtypeToUpdate: unitSubtypeEnum;

        const newMix = buildingInitProps.unitWishList.map(m => {
            if (m.type === toggleType) {
                m.active = !m.active;
                m.dirty = true;
                subtypeToUpdate = m.subtype;
                if (!m.active) {
                    m.percentage = 0;
                } else {
                    if (m.subtype === unitSubtypeEnum.apartment) {
                        m.percentage = 100 - totalPercentageApartments;
                    } else if (m.subtype === unitSubtypeEnum.maisonette) {
                        m.percentage = 100 - totalPercentageMaisonettes;
                    }
                }
                return { ...m };
            } else {
                return { ...m };
            }
        });

        computeCleanPercentages(newMix, subtypeToUpdate);

        setBuildingInitProps({ ...buildingInitProps, unitWishList: newMix });
    }


    const changePercentageAndUpdateNonDirtyIfPossible = (type: unitBaseTypeEnum, percentage: number) => {
        let subtypeToUpdate: unitSubtypeEnum;

        const newMix = buildingInitProps.unitWishList.map(m => {
            if (m.type === type) {
                m.percentage = percentage;
                m.dirty = true;
                subtypeToUpdate = m.subtype;
                return { ...m };
            } else {
                return { ...m };
            }
        });

        computeCleanPercentages(newMix, subtypeToUpdate);

        setBuildingInitProps({ ...buildingInitProps, unitWishList: newMix });
    }

    const getDataModelForUnitBaseType = (unitBaseType: unitBaseTypeEnum, extend: UnitWish): UnitWishExtension => {
        let result = { ...additionalDataForBaseUnitType.find(t => t.type === unitBaseType) };
        const unitsOfBaseType = unitsJSON.filter(s => s.baseType === unitBaseType);
        const squareMetersOfBaseTypes = unitsOfBaseType.map(s => Math.floor(s.UFA));
        const min = Math.min(...squareMetersOfBaseTypes);
        const max = Math.max(...squareMetersOfBaseTypes);
        if (min === max) {
            result.squareMeters = [min];
        } else {
            result.squareMeters = [min, max];
        }
        return { ...extend, ...result };
    }

    const getWoonmixModelBasedOnType = (type: buildingType) => {
        let result: UnitWishExtension[];

        if (buildingInitProps.state === activeStateEnum.editUnitWishList) {
            result = [...buildingInitProps.editUnitWishList.map(s => getDataModelForUnitBaseType(s.type, s))];
            if (type === buildingType.rowhouse) {
                setApartmentType(unitSubtypeEnum.maisonette);
            }
        } else {
            switch (type) {
                case buildingType.hybrid:
                    result = [...defaultUnitWishListHybrid.map(s => getDataModelForUnitBaseType(s.type, s))];
                    break;
                case buildingType.doubleLoadedCorridor:
                    result = [...defaultUnitWishListCorridor.map(s => getDataModelForUnitBaseType(s.type, s))];
                    break;
                case buildingType.singleLoadedCorridor:
                    result = [...defaultUnitWishListGallery.map(s => getDataModelForUnitBaseType(s.type, s))];
                    break;
                case buildingType.rowhouse:
                    result = [...defaultUnitWishListRowhouse.map(s => getDataModelForUnitBaseType(s.type, s))];
                    setApartmentType(unitSubtypeEnum.maisonette);
                    break;
                case buildingType.tower:
                    result = [...defaultUnitWishListTower.map(s => getDataModelForUnitBaseType(s.type, s))];
                    break;
                default:
                    break;
            }
        }
        setLivingMix(result);
        return result;
    }

    const setUpUIForType = (type: buildingType) => {
        const apartments = buildingInitProps.unitWishList.filter(w => w.subtype === unitSubtypeEnum.apartment);
        const maisonettes = buildingInitProps.unitWishList.filter(w => w.subtype === unitSubtypeEnum.maisonette);

        return (
            <>
                {apartments.length > 0 && maisonettes.length > 0 &&
                    <div className={styles.subnav}>
                        <button className={apartmentType === unitSubtypeEnum.apartment ? styles.active + ' ' + styles.navBtn : styles.navBtn} onClick={() => setApartmentType(unitSubtypeEnum.apartment)}>Appartementen</button> &gt;
                        <button className={apartmentType === unitSubtypeEnum.maisonette ? styles.active + ' ' + styles.navBtn : styles.navBtn} onClick={() => setApartmentType(unitSubtypeEnum.maisonette)}>Maisonettes</button>
                    </div>
                }
                <div className={styles.content}>
                    {
                        apartmentType === unitSubtypeEnum.apartment &&
                        <>
                            {apartments.map(a => <LivingMixUnitCard buildingType={ServiceManager.buildingHelperService.getNameBuildingType(buildingInitProps.type, true)} key={a.type} data={a}
                                onClick={(type: unitBaseTypeEnum) => { toggleActiveLivingMixType(type) }}
                                onChange={(type: unitBaseTypeEnum, percentage: number) => { changePercentageAndUpdateNonDirtyIfPossible(type, percentage) }} />)}
                        </>
                    }
                    {
                        apartmentType === unitSubtypeEnum.maisonette &&
                        <>
                            {maisonettes.map(m => <LivingMixUnitCard buildingType={ServiceManager.buildingHelperService.getNameBuildingType(buildingInitProps.type, true)} key={m.type} data={m}
                                onClick={(type: unitBaseTypeEnum) => { toggleActiveLivingMixType(type) }}
                                onChange={(type: unitBaseTypeEnum, percentage: number) => { changePercentageAndUpdateNonDirtyIfPossible(type, percentage) }} />)}
                        </>
                    }

                </div>
                <div className={styles.total} data-explanation-id={explanationIds.buildingLivingMix}>
                    {apartments.length > 0 && maisonettes.length > 0 &&
                        <>
                            {
                                apartmentType === unitSubtypeEnum.apartment &&
                                <span className={totalPercentageApartments !== 100 ? styles.error : ''}>
                                    TOTAAL {totalPercentageApartments} %
                                </span >
                            }
                            {
                                apartmentType === unitSubtypeEnum.maisonette &&
                                <span className={totalPercentageMaisonettes !== 100 ? styles.error : ''}>
                                    TOTAAL {totalPercentageMaisonettes} %

                                </span>
                            }
                            {
                                totalPercentageApartments !== 100 &&
                                <div className={styles.errorText}>
                                    Appartementen
                                    {
                                        100 - totalPercentageApartments > 0 ? ` missen  ${100 - totalPercentageApartments} ` : ` overschrijden ${Math.abs(100 - totalPercentageApartments)} `
                                    }
                                    %
                                </div>
                            }
                            {
                                totalPercentageMaisonettes !== 100 &&
                                <div className={styles.errorText}>
                                    Maisonettes
                                    {
                                        100 - totalPercentageMaisonettes > 0 ? ` missen  ${100 - totalPercentageMaisonettes} ` : ` overschrijden ${Math.abs(100 - totalPercentageMaisonettes)} `
                                    }
                                    %
                                </div>
                            }
                        </>
                    }
                    {
                        apartments.length > 0 && maisonettes.length === 0 &&
                        <>
                            <span className={totalPercentageApartments !== 100 ? styles.error : ''}>
                                TOTAAL {totalPercentageApartments} %
                            </span>
                            {
                                totalPercentageApartments !== 100 &&
                                <div className={styles.errorText}>
                                    {
                                        100 - totalPercentageApartments > 0 ? ` missen  ${100 - totalPercentageApartments} ` : ` overschrijden ${Math.abs(100 - totalPercentageApartments)} `
                                    }
                                    %
                                </div>
                            }
                        </>
                    }
                    {
                        apartments.length === 0 && maisonettes.length > 0 &&
                        <>
                            <span className={totalPercentageMaisonettes !== 100 ? styles.error : ''}>
                                TOTAAL {totalPercentageMaisonettes} %
                            </span>
                            {
                                totalPercentageMaisonettes !== 100 &&
                                <div className={styles.errorText}>
                                    {
                                        100 - totalPercentageMaisonettes > 0 ? ` missen  ${100 - totalPercentageMaisonettes} ` : ` overschrijden ${Math.abs(100 - totalPercentageMaisonettes)} `
                                    }
                                    %
                                </div>
                            }
                        </>
                    }
                </div>
            </>
        )
    }

    const setupButton = () => {
        const apartments = buildingInitProps.unitWishList.filter(w => w.subtype === unitSubtypeEnum.apartment);
        const maisonettes = buildingInitProps.unitWishList.filter(w => w.subtype === unitSubtypeEnum.maisonette);
        let res;

        let buttonTitle = 'Plaats Gebouw';
        if (buildingInitProps.state !== activeStateEnum.add) {
            buttonTitle = 'Gebouw Wijzigen';
        }

        if (apartments.length > 0 && maisonettes.length === 0) {
            res = <Button color="primary" variant="contained" onClick={handleFinished} disabled={totalPercentageApartments !== 100}>{buttonTitle}</Button>;
        } else if (apartments.length === 0 && maisonettes.length > 0) {
            res = <Button color="primary" variant="contained" onClick={handleFinished} disabled={totalPercentageMaisonettes !== 100}>{buttonTitle}</Button>;
        } else if (apartments.length > 0 && maisonettes.length > 0) {
            res = <Button color="primary" variant="contained" onClick={handleFinished} disabled={totalPercentageApartments !== 100 || totalPercentageMaisonettes !== 100}>{buttonTitle}</Button>
        }
        return res;
    }

    return (
        <>
            <DialogTitle>
                <IconButton onClick={handleClose} className={styles.close} color="secondary" data-testid="close-dialog" aria-label="Dialog dicht" component="span">
                    <Close />
                </IconButton>
                <div>{`WOONMIX ${ServiceManager.buildingHelperService.getNameBuildingType(buildingInitProps.type, true)}`}</div>
            </DialogTitle>
            <DialogContent>
                {setUpUIForType(buildingInitProps.type)}
            </DialogContent>
            <DialogActions>
                <div className={styles.previous}>
                    {
                        buildingInitProps.state !== activeStateEnum.editUnitWishList &&
                        < Button color="secondary" variant="outlined" onClick={handlePrevious}>Vorige</Button>
                    }
                </div>
                <div style={{ flex: '1 0 0' }} />
                <div className={styles.next}>
                    {setupButton()}
                </div>
            </DialogActions>
        </>
    );
}

export default LivingMixSelection;
